import React, { createRef, useEffect, useState } from 'react';
import { styled } from '@this/constants/themes';
import { observer } from 'mobx-react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import type Calendar from '@this/domain/appoint/calendar';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import InputLabel from '@material-ui/core/InputLabel';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import muiTheme from '@this/components/appoint/mui_theme';

interface Props {
  isLoading: boolean;
  participants: Calendar[];
  calendars: Calendar[];
  onAdd: (p: Calendar) => void;
  onRemove: (p: Calendar) => void;
}

const ParticipantInput = observer(({ isLoading, participants, calendars, onAdd, onRemove }: Props) => {
  const [isAddingParticipant, setIsAddingParticipant] = useState<boolean>(false);
  const [addParticipantText, setAddParticipantText] = useState<string>('');
  // 「+参加者を追加」ボタンクリック時に、テキストフィールドにフォーカスをあてる
  const addParticipantRef = createRef<HTMLDivElement>();
  useEffect(() => {
    if (isAddingParticipant && addParticipantRef.current) {
      const inputs = addParticipantRef.current.getElementsByTagName('input');
      const input = inputs[0];
      if (input) {
        input.focus();
      }
    }
  }, [isAddingParticipant]);
  return (
    <Box display="flex">
      <Box flexGrow={999}>
        <InputLabel shrink>
          <span style={{ display: 'inline-block', verticalAlign: 'top', marginRight: '10px' }}>参加者</span>
          {isLoading && (
            <img
              src="/images/loading.gif"
              width={20}
              height={20}
              style={{ display: 'inline-block', verticalAlign: 'top', marginTop: '-2px' }}
            />
          )}
        </InputLabel>
        <Autocomplete
          options={calendars}
          renderOption={option => (
            <ParticipantOption>
              <ParticipantIcon />
              <div>
                <ParticipantMainText>{option.name}</ParticipantMainText>
                <ParticipantSubText>{option.provider === 'outlook' ? option.email : option.id}</ParticipantSubText>
              </div>
            </ParticipantOption>
          )}
          inputValue={addParticipantText}
          filterOptions={(options, state) => {
            const q = state.inputValue;
            return options.filter(
              o =>
                (o.id.indexOf(q) > -1 || o.name.indexOf(q) > -1) &&
                participants.findIndex(p => p.id === o.id) === -1
            );
          }}
          onInputChange={(_event, value, reason) => {
            if (reason === 'input') {
              setAddParticipantText(value);
            }
          }}
          onChange={(_event, selected) => {
            setAddParticipantText('');
            if (selected) {
              onAdd(selected);
              setIsAddingParticipant(false);
            }
          }}
          openOnFocus
          renderInput={params => (
            <TextField
              {...params}
              ref={addParticipantRef}
              placeholder="参加者を追加"
              style={{ fontSize: '12px' }}
              onBlur={() => setIsAddingParticipant(false)}
            />
          )}
        />
        <ParticipantList>
          {participants.map(p => (
            <ParticipantBox key={p.id}>
              <Disc style={{ backgroundColor: p.backgroundColor || muiTheme.palette.secondary.main }} />
              <ParticipantName>{p.name}</ParticipantName>
              <DeleteButton size="small" onClick={() => onRemove(p)}>
                <CloseIcon fontSize="small" style={{ color: '#fff' }} />
              </DeleteButton>
            </ParticipantBox>
          ))}
        </ParticipantList>
      </Box>
    </Box>
  );
});

// Participant
export const ParticipantOption = styled.div`
  display: flex;
  align-items: center;
`;

export const ParticipantMainText = styled.div`
  font-size: 0.9rem;
  white-space: nowrap;
`;

export const ParticipantSubText = styled.div`
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.54);
`;

export const ParticipantIcon = styled(AccountCircleIcon)`
  width: 15px;
  height: 18px;
  display: block;
  margin-right: 10px;
`;

export const ParticipantList = styled.div`
  margin-bottom: 8px;
`;

const ParticipantBox = styled(Box)`
  display: flex;
  align-items: center;
  padding-top: 4px;
`;

const Disc = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 7px;
`;

const ParticipantName = styled(Typography)`
  margin-left: 6px;
  color: #606060;
  font-size: 12px;
`;

const DeleteButton = styled(IconButton)`
  height: 15px;
  margin: -1px 0 0 8px;
  padding: 2px;
  background-color: #ddd;

  .MuiSvgIcon-fontSizeSmall {
    height: 11px;
    font-size: 11px;
    line-height: 1;
  }
`;

export default ParticipantInput;
